import React from 'react'
import styles from './UserNav.module.scss'
import Logo from '@/components/Header/Logo'
import SideModal from './components/SideModal'
import { useLocale } from 'core/components/Locale'
import { useHCRouter } from '@/components/HCNextLink'
import CollapsiblePanel from '@/components/CollapsiblePanel'
import Overview from '../UserPanel/Overview'
import AssetsOverview from '../UserPanel/AssetsOverview'
import { useQueryClient } from '@tanstack/react-query'
import { Authentication, useAuthenticate } from 'core/components/Authenticate'
import { useResponsiveDevice } from '@/hooks/useResponsiveDevice'
import { userPanelItems, userNavItems } from './config'

type SideNavProps = {
  isOpen: boolean
  onClose: () => void
}

const SideNav: React.FC<SideNavProps> = ({ isOpen, onClose }) => {
  const t = useLocale()
  const router = useHCRouter()
  const queryClient = useQueryClient()
  const responsiveDevice = useResponsiveDevice()
  const { loginData } = useAuthenticate()

  const handleNavigation = (item: { link: string; mLink?: string }) => {
    if (!loginData) {
      router.push(`login`)
      return
    }
    if (responsiveDevice === 'phone' && item.mLink) {
      router.push(item.mLink)
      return
    }
    router.push(item.link)
  }

  const panelItems = userPanelItems.map((panel) => ({
    key: panel.key,
    className: styles.panel,
    title: (
      <div className={styles.panelTitle}>
        <i className={`${styles.icon} ${styles[panel.icon]}`}></i>
        <span>{t(panel.locale)}</span>
      </div>
    ),
    contentClassName: styles.panelContent,
    children: (
      <>
        {panel.key === 'assets' && (
          <div className={styles.assets}>
            <AssetsOverview visible={isOpen} />
          </div>
        )}
        {panel.items.map((item) => (
          <div key={item.key} onClick={() => handleNavigation(item)} className={styles.menuItem}>
            {t(item.locale)}
          </div>
        ))}
      </>
    ),
  }))

  return (
    <SideModal isOpen={isOpen} onClose={onClose}>
      <div className={styles.header}>
        <div className={styles.logoWrap}>
          <div className="show-phone">
            <Logo />
          </div>
          <i onClick={onClose} className={`${styles.closeBtn} iconfont iconguanbi`}></i>
        </div>
      </div>

      <div className={styles.menuContainer}>
        <div className={styles.userSection}>{isOpen && <Overview />}</div>

        <CollapsiblePanel items={panelItems} accordion={true} />

        <Authentication
          allow={({ logout }) => {
            const handleLogout = () => {
              queryClient.clear()
              logout()
            }

            return (
              <>
                {userNavItems.map((item, i) => (
                  <div className={styles.navItem} key={i} onClick={() => handleNavigation(item)}>
                    <i className={`${styles.icon} ${styles[item.icon]}`}></i>
                    <span className={styles.label}>{t(item.locale)}</span>
                  </div>
                ))}

                <div className={styles.logout}>
                  <button onClick={handleLogout} className={styles.logoutButton}>
                    {t('header_055')}
                  </button>
                </div>
              </>
            )
          }}
        />
      </div>
    </SideModal>
  )
}

export default SideNav
